import type AgendaItem from "@/domain/entities/agenda/AgendaItem";
import type Email from "@/domain/entities/emails/Email";
import type { MailsListId } from "@/domain/entities/emails/Email";
import type MailDraft from "@/domain/entities/emails/MailDraft";
import type MailActionType from "@/domain/enums/MailActionType";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    activateTab,
    closeGroup,
    closeTab,
    eventTabKey,
    mailTabKey,
    openTab,
    updateTab,
    type MailTab,
    type PaneKind,
    type PaneTab,
    type SidePanes,
} from "./sidePanes";

/**
 * What is opened from the chat, in the side panel: the panes, the openers the chat calls (a mail, a
 * draft, an event) and what the panel does to its tabs.
 */
const useSidePanes = () => {
    const { t } = useTranslation();
    const [panes, setPanes] = useState<SidePanes>([]);

    const openers = useMemo(() => {
        const subjectOf = (mail: Email) =>
            mail.subject || t("vistassistant.sidePanel.noSubject");
        const mailTab = (mail: Email, listId: MailsListId): MailTab => ({
            kind: "mail",
            key: mailTabKey(mail),
            title: subjectOf(mail),
            mail,
            listId,
        });

        return {
            onMailClick: (mail: Email, listId: MailsListId) =>
                setPanes((open) => openTab(open, mailTab(mail, listId))),
            onComposeDraft: (
                key: string,
                draft: MailDraft,
                replyTo?: {
                    mail: Email;
                    listId: MailsListId;
                    actionType: MailActionType;
                },
            ) =>
                // Only the composer, the whole height for it: the mail a reply answers stays one
                // click away (« En réponse à » on the draft's card).
                setPanes((open) =>
                    openTab(open, {
                        kind: "compose",
                        key: `compose:${key}`,
                        title: replyTo
                            ? t("vistassistant.sidePanel.replyTitle", {
                                  subject: subjectOf(replyTo.mail),
                              })
                            : draft.subject ||
                              t("vistassistant.sidePanel.newMail"),
                        draft,
                        replyTo,
                    }),
                ),
            onEventClick: (event: AgendaItem) =>
                setPanes((open) =>
                    openTab(open, {
                        kind: "event",
                        key: eventTabKey(event),
                        title:
                            event.subject || t("vistassistant.untitledEvent"),
                        event,
                    }),
                ),
        };
    }, [t]);

    // Stable (setPanes is): the side panel, protected by React.memo, does not re-render on every
    // keystroke in the assistant's input (audit de performance du 15/10, P4-05).
    const paneActions = useMemo(
        () => ({
            onActivate: (kind: PaneKind, key: string) =>
                setPanes((open) => activateTab(open, kind, key)),
            onCloseGroup: (kind: PaneKind) =>
                setPanes((open) => closeGroup(open, kind)),
            onCloseTab: (kind: PaneKind, key: string) =>
                setPanes((open) => closeTab(open, kind, key)),
            onUpdateTab: (tab: PaneTab) =>
                setPanes((open) => updateTab(open, tab)),
        }),
        [],
    );

    return { panes, openers, paneActions };
};

export default useSidePanes;
